import { useState, useCallback } from 'react';
import '@chatscope/chat-ui-kit-styles/dist/default/styles.min.css';
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  TypingIndicator,
} from '@chatscope/chat-ui-kit-react';
import { useAuth } from '../context/AuthContext';
import { SuggestedQuestions } from './SuggestedQuestions';
import { config } from '../config';
import { MarkdownMessage } from './MarkdownMessage';

interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
}

export function ChatWidget() {
  const { login, isAuthenticated, getValidToken } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      content: isAuthenticated
        ? "Hello! I'm your assistant. I can help you search customers, look up orders, check inventory, and more. How can I help you today?"
        : "Hello! I'm your assistant. Sign in below to access SAP Business One tools including customer search, order lookup, inventory, and more.",
      sender: 'assistant',
      timestamp: new Date(),
    },
  ]);
  const [isTyping, setIsTyping] = useState(false);

  const sendMessage = useCallback(
    async (content: string) => {
      // Add user message
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        content,
        sender: 'user',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsTyping(true);

      try {
        // Get a valid token (will automatically refresh if expired)
        const token = await getValidToken();

        const response = await fetch(`${config.apiUrl}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            message: content,
            history: messages.map((m) => ({
              role: m.sender === 'user' ? 'user' : 'assistant',
              content: m.content,
            })),
          }),
        });

        if (response.status === 401) {
          // Token expired or invalid — prompt re-login
          const authMessage: ChatMessage = {
            id: Date.now().toString(),
            content:
              'Your session has expired. Please sign in again to continue.',
            sender: 'assistant',
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, authMessage]);
          setIsTyping(false);
          return;
        }

        const data = await response.json();

        // Add assistant response
        const assistantMessage: ChatMessage = {
          id: Date.now().toString(),
          content: data.message,
          sender: 'assistant',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } catch (error) {
        console.error('Failed to send message:', error);
        const errorMessage: ChatMessage = {
          id: Date.now().toString(),
          content: 'Sorry, something went wrong. Please try again.',
          sender: 'assistant',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsTyping(false);
      }
    },
    [getValidToken, messages]
  );

  // Strip HTML tags from user input
  const stripHtml = (html: string): string => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const handleSend = (text: string) => {
    const cleanText = stripHtml(text).trim();
    if (cleanText) {
      sendMessage(cleanText);
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    sendMessage(question);
  };

  const handleLogin = async () => {
    await login();
    // Update welcome message to reflect authenticated state
    setMessages((prev) => {
      if (prev.length === 1 && prev[0].id === '1') {
        return [{
          ...prev[0],
          content: "You're signed in! I can help you search customers, look up orders, check inventory, and more. How can I help you today?",
        }];
      }
      return prev;
    });
  };

  const showLoginButton = !isAuthenticated;
  const showSuggestions = messages.length === 1;

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative', overflow: 'hidden' }}>
      <MainContainer>
        <ChatContainer>
          <MessageList
            typingIndicator={
              isTyping ? <TypingIndicator content="Assistant is typing..." /> : undefined
            }
          >
            {messages.map((msg) => (
              <Message
                key={msg.id}
                model={{
                  message: '',
                  sender: msg.sender,
                  direction: msg.sender === 'user' ? 'outgoing' : 'incoming',
                  position: 'single',
                }}
              >
                <Message.CustomContent>
                  <MarkdownMessage content={msg.content} sender={msg.sender} />
                </Message.CustomContent>
              </Message>
            ))}

            {showLoginButton && (
              <div style={{ padding: '1rem', textAlign: 'center' }}>
                <button
                  onClick={handleLogin}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                  }}
                >
                  Sign In Securely
                </button>
              </div>
            )}

            {showSuggestions && (
              <div style={{ padding: '1rem' }}>
                <SuggestedQuestions onSelect={handleSuggestedQuestion} />
              </div>
            )}
          </MessageList>

          <MessageInput
            placeholder={isAuthenticated ? "Type your message..." : "Sign in to start chatting..."}
            onSend={handleSend}
            attachButton={false}
            disabled={!isAuthenticated}
          />
        </ChatContainer>
      </MainContainer>
    </div>
  );
}
